import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Shirt, Moon, Droplets, CheckCircle2, Download, Clock, ShieldCheck, Star, XIcon, LockIcon } from 'lucide-react';
import { PromoBanner } from './components/PromoBanner';
import { RecentPurchaseToast } from './components/RecentPurchaseToast';
import { SocialTrustEngine } from './components/SocialTrustEngine';
import { PsychologicalPricing } from './components/PsychologicalPricing';
import { TwoPathsClose } from './components/TwoPathsClose';
import { FaqAccordion } from './components/FaqAccordion';
import { FadeInUp } from './components/FadeInUp';
const PRIVACY_CONTENT = `
Última actualización: Abril 2026

En Método Anti-Inflamatorio 7D, respetamos tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

**Información que recopilamos:**
• Datos de compra: nombre, correo electrónico y datos de pago procesados de forma segura a través de PayPal.
• No almacenamos información de tarjetas de crédito ni datos financieros sensibles.

**Uso de la información:**
• Para procesar tu compra y entregar el producto digital.
• Para enviarte actualizaciones relacionadas con tu compra (si aplica).
• No vendemos, alquilamos ni compartimos tu información personal con terceros.

**Protección de datos:**
• Utilizamos medidas de seguridad estándar de la industria para proteger tu información.
• Los pagos son procesados de forma segura a través de PayPal.

**Cookies:**
• Podemos utilizar cookies básicas para mejorar tu experiencia de navegación.

**Tus derechos:**
• Puedes solicitar la eliminación de tus datos en cualquier momento.
• Puedes optar por no recibir comunicaciones de marketing.

**Cambios a esta política:**
• Nos reservamos el derecho de actualizar esta política. Los cambios serán publicados en esta página.
`.trim();
const TERMS_CONTENT = `
Última actualización: Abril 2026

Al comprar y utilizar el Método Anti-Inflamatorio 7D, aceptas los siguientes términos y condiciones.

**Producto digital:**
• Este es un producto digital (PDF) que se entrega electrónicamente después de la compra.
• Nuestra política estándar es que los productos digitales no son reembolsables, SIN EMBARGO, para el Método 7D honramos nuestra Garantía de Satisfacción de 7 días descrita en la página de venta.

**Uso personal:**
• El contenido es para uso personal únicamente.
• No está permitido redistribuir, revender o compartir el material sin autorización.

**Descargo de responsabilidad médica:**
• Este producto es solo para fines informativos y educativos.
• No sustituye el consejo médico profesional, diagnóstico o tratamiento.
• Consulta siempre a un profesional de la salud antes de comenzar cualquier programa de alimentación o bienestar.
• Los resultados pueden variar de persona a persona.

**Propiedad intelectual:**
• Todo el contenido, diseño y material del Método Anti-Inflamatorio 7D está protegido por derechos de autor.

**Limitación de responsabilidad:**
• No nos hacemos responsables de resultados individuales ni de decisiones tomadas basándose en el contenido del producto.

**Ley aplicable:**
• Estos términos se rigen por las leyes aplicables de los Estados Unidos.
`.trim();
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
export function App() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    // Preconnect to critical origins for faster resource loading
    const origins = ['https://cdn.magicpatterns.com', 'https://connect.facebook.net'];
    origins.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
    // Meta Pixel initialization (immediate — no delay to preserve ad metrics)
    if (typeof window.fbq === 'function') return;
    const n: any = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(t);
    window.fbq('init', '1666572947695847');
    window.fbq('track', 'PageView');
  }, []);
  const handleAddToCart = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'AddToCart', {
        content_name: 'Método Anti-Inflamatorio 7D',
        content_type: 'product',
        value: 6.36,
        currency: 'USD'
      });
    }
  };
  return <div className="min-h-screen bg-cream font-sans text-charcoal selection:bg-gold selection:text-white pt-[68px]" data-id="element-0">
      <PromoBanner data-id="element-1" />
      <RecentPurchaseToast data-id="element-2" />
      {/* 1. HERO SECTION */}
      <section className="pt-4 pb-12 px-5 md:pt-16 md:pb-28 max-w-5xl mx-auto text-center" data-id="element-3">
        <FadeInUp data-id="element-4">
          <span className="inline-block py-1 px-3 rounded-full bg-sage text-white font-semibold text-xs tracking-wide mb-4 shadow-sm" data-id="element-5">
            Para la mujer latina que vive en USA
          </span>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-[#1D2E28] max-w-4xl mx-auto" data-id="element-6">
            Recupera tu abdomen ligero en las próximas 168 horas
            <span className="block text-lg md:text-2xl font-medium text-charcoal/70 mt-2 font-sans" data-id="element-7">
              (sin dietas que no puedes seguir)
            </span>
          </h1>
          <p className="text-base md:text-lg text-charcoal/80 mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-8">
            El Protocolo 3-3-3: Comidas simples, tés específicos y micro-hábitos
            de minutos. Digital, al grano y con ingredientes de Walmart o
            Target.
          </p>

          <div className="flex gap-2 mb-5 items-center justify-center" data-id="element-9">
            <div className="flex text-gold" data-id="element-10">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-11" />)}
            </div>
            <span className="text-charcoal/60 font-medium text-xs" data-id="element-12">
              +1,200 mujeres ya la usan
            </span>
          </div>

          <a href="#offer" onClick={e => {
          e.preventDefault();
          document.getElementById('offer')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="shimmer-btn w-full md:w-auto font-bold text-base md:text-xl py-4 px-8 md:px-12 rounded-2xl shadow-xl transition-all transform hover:scale-105 flex items-center justify-center mx-auto group mb-2 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" data-id="element-13">
            <span data-id="element-14">¡Sí, quiero el Protocolo!</span>
            <span className="mx-1.5" data-id="element-15">·</span>
            <span data-id="element-16">$6.36 USD</span>
            <ArrowRight className="ml-2 w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform shrink-0" data-id="element-17" />
          </a>

          <div className="flex items-center justify-center gap-1.5 mb-10" data-id="element-18">
            <LockIcon className="w-3.5 h-3.5 text-charcoal/50" data-id="element-19" />
            <span className="text-xs md:text-sm text-charcoal/60 font-semibold tracking-wide uppercase" data-id="element-20">
              Pago único — Sin suscripciones
            </span>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-21">
          <div className="relative mx-auto max-w-3xl" data-id="element-22">
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cream to-transparent z-10" data-id="element-23"></div>
            <img src="https://cdn.magicpatterns.com/uploads/aoUnQchMG3dqd9JvoHVniN/heylloyd-image-1775268223226_(2).webp" alt="Método Anti-Inflamatorio 7D" width={1024} height={1024} fetchPriority="high" decoding="async" className="w-full h-auto rounded-3xl shadow-2xl object-cover border-4 border-white" data-id="element-24" />
          </div>
        </FadeInUp>
      </section>

      {/* 2. AGITACIÓN */}
      <section className="py-20 px-6 bg-white" data-id="element-25">
        <div className="max-w-5xl mx-auto" data-id="element-26">
          <FadeInUp data-id="element-27">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-16 text-center text-deep-green" data-id="element-28">
              Si esto te pasa, no estás sola
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-29">
            <FadeInUp delay={0.1} data-id="element-30">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-31">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 text-sage shadow-sm" data-id="element-32">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-33">
                    <path d="M6 2h12l-1 9-2-1v12H9V10L7 11z" data-id="element-34" />
                    <path d="M12 2v8" data-id="element-35" />
                  </svg>
                </div>
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-36">
                  En la mañana tu jean cierra perfecto. Pero llega la tarde y
                  sientes que te aprieta, te molesta, y no entiendes qué cambió.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-37">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-38">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 text-sage shadow-sm" data-id="element-39">
                  <Moon className="w-7 h-7" data-id="element-40" />
                </div>
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-41">
                  Intentas comer bien, tomar agua, evitar lo que "engorda"… pero
                  la hinchazón sigue ahí cada noche, como si nada funcionara.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-42">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-43">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 text-sage shadow-sm" data-id="element-44">
                  <Droplets className="w-7 h-7" data-id="element-45" />
                </div>
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-46">
                  Te acuestas cansada, pesada, incómoda. Y al día siguiente
                  vuelve a pasar lo mismo. Es frustrante.
                </p>
              </div>
            </FadeInUp>
          </div>

          <FadeInUp delay={0.4} data-id="element-47">
            <div className="max-w-3xl mx-auto text-center" data-id="element-48">
              <p className="text-xl md:text-2xl font-serif italic text-deep-green/90 leading-relaxed" data-id="element-49">
                "La buena noticia es que esa hinchazón no tiene que ser tu
                normal. Tu cuerpo puede sentirse más ligero cuando le das lo que
                realmente necesita — y es más simple de lo que crees."
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 3. PRESENTACIÓN DEL MECANISMO */}
      <section className="py-20 px-6 bg-cream" data-id="element-50">
        <div className="max-w-4xl mx-auto" data-id="element-51">
          <FadeInUp data-id="element-52">
            <div className="text-center mb-16" data-id="element-53">
              <span className="inline-block py-1 px-4 rounded-full bg-sage/20 text-sage font-bold text-sm tracking-wider uppercase mb-6" data-id="element-54">
                Qué incluye
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-deep-green" data-id="element-55">
                Un plan de 7 días diseñado
                <br data-id="element-56" />
                para tu ritmo de vida real
              </h2>
              <p className="text-lg md:text-xl text-charcoal/70 max-w-2xl mx-auto" data-id="element-57">
                No es un curso largo ni un PDF genérico. Es una guía paso a paso
                con todo lo que necesitas para empezar hoy — pensada para la
                mujer que trabaja, cocina rápido y no tiene tiempo de sobra.
              </p>
            </div>
          </FadeInUp>

          <div className="space-y-6" data-id="element-58">
            <FadeInUp delay={0.1} data-id="element-59">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-60">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-61">
                  <CheckCircle2 className="w-8 h-8" data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-64">
                    Plan de comidas de 7 días
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-65">
                    Recetas simples con ingredientes que encuentras en Walmart o
                    Target. Rápidas de preparar, pensadas para reducir la
                    hinchazón.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-66">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-67">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-68">
                  <CheckCircle2 className="w-8 h-8" data-id="element-69" />
                </div>
                <div data-id="element-70">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-71">
                    3 infusiones diarias
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-72">
                    Tés e infusiones específicas que apoyan tu digestión y
                    ayudan a que tu cuerpo se sienta menos pesado durante el
                    día.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-73">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-74">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-75">
                  <CheckCircle2 className="w-8 h-8" data-id="element-76" />
                </div>
                <div data-id="element-77">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-78">
                    Hábitos de 3 minutos al día
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-79">
                    Pequeñas rutinas que puedes hacer en cualquier momento para
                    ayudar a tu cuerpo a manejar mejor el estrés y la tensión
                    diaria.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 4. TESTIMONIOS DE TRANSFORMACIÓN */}
      <section className="py-20 px-6 bg-white" data-id="element-80">
        <div className="max-w-6xl mx-auto" data-id="element-81">
          <FadeInUp data-id="element-82">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-16 text-center text-deep-green max-w-2xl mx-auto" data-id="element-83">
              Mujeres reales, experiencias reales
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-84">
            <FadeInUp delay={0.1} data-id="element-85">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-86">
                <div className="flex text-gold mb-6" data-id="element-87">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-88" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-89">
                  "Trabajo doble turno limpiando casas y llegaba a mi casa tan
                  hinchada que me cambiaba a pants apenas entraba. Pensé que era
                  normal por mi edad. Empecé la guía un lunes y para el jueves
                  ya sentía la diferencia. Nada complicado, todo con cosas que
                  ya tenía en la cocina."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-90">
                  <div data-id="element-91">
                    <p className="font-bold text-deep-green" data-id="element-92">María T.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-93">Florida</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-94">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-95">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-96">
                <div className="flex text-gold mb-6" data-id="element-97">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-98" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-99">
                  "Yo ya había probado jugos verdes, detox, de todo. Nada me
                  duraba más de 3 días. Lo que me gustó de esto es que no te
                  pide cosas raras — son comidas normales y unos tés que de
                  verdad se sienten bien. Al quinto día mi esposo me dijo que se
                  me veía el abdomen más plano."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-100">
                  <div data-id="element-101">
                    <p className="font-bold text-deep-green" data-id="element-102">Elena R.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-103">Arizona</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-104">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-105">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-106">
                <div className="flex text-gold mb-6" data-id="element-107">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-108" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-109">
                  "Soy mamá de 3 y no tengo tiempo para nada elaborado. Esto lo
                  pude seguir sin cambiar mi rutina. Lo que más me sorprendió es
                  que dormí mejor desde el segundo día. Ya no me acuesto con esa
                  sensación de pesadez que me tenía harta. Por $6 valió
                  completamente la pena."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-110">
                  <div data-id="element-111">
                    <p className="font-bold text-deep-green" data-id="element-112">Carmen S.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-113">Texas</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-114">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      <SocialTrustEngine data-id="element-115" />
      <PsychologicalPricing data-id="element-116" />

      {/* 5. OFERTA Y PRECIO */}
      <section id="offer" className="py-20 px-6 bg-cream" data-id="element-117">
        <div className="max-w-3xl mx-auto" data-id="element-118">
          <FadeInUp data-id="element-119">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl border-2 border-dashed border-gold/50 relative text-center" data-id="element-120">
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-deep-green text-white py-2 px-6 rounded-full font-bold text-sm tracking-wider shadow-md" data-id="element-121">
                OFERTA DE LANZAMIENTO
              </div>

              <h3 className="font-serif text-2xl md:text-3xl font-bold text-deep-green mt-6 mb-8" data-id="element-122">
                Todo lo que recibes hoy:
              </h3>

              <div className="text-left max-w-md mx-auto space-y-4 mb-10" data-id="element-123">
                <div className="flex items-start gap-3" data-id="element-124">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-125" />
                  <div data-id="element-126">
                    <span className="font-bold text-charcoal" data-id="element-127">
                      Plan de comidas completo de 7 días
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-128">
                      — valor $9.99
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-129">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-130" />
                  <div data-id="element-131">
                    <span className="font-bold text-charcoal" data-id="element-132">
                      Guía de 3 infusiones diarias
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-133">
                      — valor $4.99
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-134">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-135" />
                  <div data-id="element-136">
                    <span className="font-bold text-charcoal" data-id="element-137">
                      Rutina de hábitos de 3 minutos
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-138">
                      — valor $4.99
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-139">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-140" />
                  <div data-id="element-141">
                    <span className="font-bold text-charcoal" data-id="element-142">
                      Lista de compras lista para imprimir
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-143">
                      — valor $2.99
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4" data-id="element-144">
                <span className="text-charcoal/40 text-lg md:text-xl line-through font-medium" data-id="element-145">
                  Valor total: $22.96 USD
                </span>
              </div>

              <div className="mb-2" data-id="element-146">
                <span className="text-sage font-bold text-base tracking-widest uppercase" data-id="element-147">
                  HOY LO LLEVAS TODO POR:
                </span>
              </div>

              <div className="mb-10" data-id="element-148">
                <span className="font-serif text-7xl md:text-8xl font-bold text-gold drop-shadow-sm" data-id="element-149">
                  $6.36 <span className="text-3xl md:text-4xl" data-id="element-150">USD</span>
                </span>
              </div>

              <div className="flex items-center justify-center gap-1.5 mb-6" data-id="element-151">
                <LockIcon className="w-4 h-4 text-charcoal/50" data-id="element-152" />
                <span className="text-sm text-charcoal/70 font-semibold tracking-wide uppercase" data-id="element-153">
                  Pago único — Sin suscripciones
                </span>
              </div>

              <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8" data-id="element-154">
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-155">
                  <Download className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-156" />{' '}
                  Descarga Inmediata
                </div>
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-157">
                  <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-158" />{' '}
                  Precio de Lanzamiento
                </div>
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-159">
                  <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-160" />{' '}
                  Garantía 7 Días
                </div>
              </div>

              <motion.a href="https://www.vitarutinas.com/cart/50330442137846:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="shimmer-btn w-full font-bold text-xl py-6 px-8 rounded-2xl shadow-xl flex items-center justify-center group mb-6 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" animate={{
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} whileHover={{
              scale: 1.07
            }} whileTap={{
              scale: 0.97
            }} data-id="element-161">
                Descargar Mi Plan de 7 Días
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-162" />
              </motion.a>

              <div className="flex flex-col items-center justify-center gap-4" data-id="element-163">
                <span className="text-charcoal/60 font-medium flex items-center text-[12px]" data-id="element-164">
                  Pago único · Sin suscripciones · Acceso de por vida
                </span>
                <div className="flex gap-4 items-center" data-id="element-165">
                  {/* Visa */}
                  <div className="bg-white rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-166">
                    <svg viewBox="0 0 750 471" className="h-5" fill="none" data-id="element-167">
                      <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763H278.198z" fill="#1A1F71" data-id="element-168" />
                      <path d="M524.307 142.687c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.634 28.141 26.485 43.845 46.754 53.185 20.77 9.536 27.77 15.636 27.77 24.17-.317 13.032-16.652 18.985-32.037 18.985-21.42 0-32.794-2.966-50.376-10.283l-6.902-3.113-7.519 43.845c12.507 5.467 35.638 10.199 59.673 10.454 56.062 0 92.425-26.187 92.862-66.884.317-22.27-14.016-39.215-44.8-53.185-18.652-9.072-30.078-15.099-30.078-24.267.317-8.258 9.703-16.834 30.712-16.834 17.567-.317 30.295 3.548 40.218 7.519l4.82 2.268 7.325-42.168z" fill="#1A1F71" data-id="element-169" />
                      <path d="M661.615 138.465h-41.231c-12.773 0-22.332 3.478-27.94 16.218l-79.245 179.545h56.062l11.19-29.189h68.397l6.508 29.189h49.474l-43.215-195.763zm-65.927 126.408l28.09-73.14 4.503-12.844 7.83 12.844 16.33 73.14h-56.753z" fill="#1A1F71" data-id="element-170" />
                      <path d="M232.903 138.465L180.663 271.96l-5.565-26.867c-9.703-31.107-39.93-64.921-73.744-81.822l47.822 170.64h56.379l83.826-195.446h-56.478z" fill="#1A1F71" data-id="element-171" />
                      <path d="M131.92 138.465H46.459l-.634 3.966c66.878 16.152 111.16 55.128 129.495 101.932L156.72 155.318c-3.331-12.423-12.907-16.535-24.8-16.853z" fill="#F9A533" data-id="element-172" />
                    </svg>
                  </div>
                  {/* Mastercard */}
                  <div className="bg-white rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-173">
                    <svg viewBox="0 0 152.407 108" className="h-5" fill="none" data-id="element-174">
                      <circle cx="60.412" cy="54" r="48" fill="#EB001B" data-id="element-175" />
                      <circle cx="91.995" cy="54" r="48" fill="#F79E1B" data-id="element-176" />
                      <path d="M76.204 18.631a47.834 47.834 0 0 0-15.792 35.369A47.834 47.834 0 0 0 76.204 89.37a47.834 47.834 0 0 0 15.791-35.37 47.834 47.834 0 0 0-15.791-35.369z" fill="#FF5F00" data-id="element-177" />
                    </svg>
                  </div>
                  {/* American Express */}
                  <div className="bg-[#006FCF] rounded-lg px-2.5 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-178">
                    <svg viewBox="0 0 60 20" className="h-3.5" fill="none" data-id="element-179">
                      <text x="30" y="15" textAnchor="middle" fill="white" fontSize="13" fontWeight="800" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.5" data-id="element-180">
                        AMEX
                      </text>
                    </svg>
                  </div>
                  {/* PayPal */}
                  <div className="bg-white rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-181">
                    <svg viewBox="0 0 124 33" className="h-4" fill="none" data-id="element-182">
                      <path d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47.1 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.703.359.42.469 1.044.332 1.907z" fill="#003087" data-id="element-183" />
                      <path d="M66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317z" fill="#003087" data-id="element-184" />
                      <path d="M84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z" fill="#003087" data-id="element-185" />
                      <path d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.724-4.583a.567.567 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.703.359.42.468 1.044.332 1.907z" fill="#0070E0" data-id="element-186" />
                      <path d="M115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317z" fill="#0070E0" data-id="element-187" />
                      <path d="M119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822a.949.949 0 0 0 .939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z" fill="#0070E0" data-id="element-188" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 6. GARANTÍA DE DESINFLAMACIÓN */}
      <section className="py-20 px-6 bg-deep-green text-cream text-center" data-id="element-189">
        <div className="max-w-3xl mx-auto" data-id="element-190">
          <FadeInUp data-id="element-191">
            <ShieldCheck className="w-20 h-20 text-gold mx-auto mb-8" data-id="element-192" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-gold" data-id="element-193">
              PRUÉBALO SIN RIESGO
            </h2>
            <p className="text-lg md:text-xl leading-relaxed mb-8 text-cream/90" data-id="element-194">
              Tienes{' '}
              <strong className="text-gold font-bold" data-id="element-195">7 días completos</strong>{' '}
              para probar el método. Si sientes que no fue útil para ti, solo
              escríbenos y te devolvemos el 100% de tu dinero. Sin preguntas,
              sin formularios complicados. Así de simple.
            </p>
            <p className="text-gold font-bold tracking-widest uppercase text-lg" data-id="element-196">
              GARANTÍA DE SATISFACCIÓN DE 7 DÍAS
            </p>
          </FadeInUp>
        </div>
      </section>

      <TwoPathsClose data-id="element-197" />

      {/* 7. CTA FINAL */}
      <section id="cta-final" className="py-20 px-6 bg-cream text-center" data-id="element-198">
        <div className="max-w-3xl mx-auto" data-id="element-199">
          <FadeInUp data-id="element-200">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-deep-green" data-id="element-201">
              Hoy puede ser el día en que dejas de sentirte hinchada
            </h2>
            <p className="text-lg md:text-xl text-charcoal/70 mb-10 max-w-2xl mx-auto" data-id="element-202">
              No necesitas otra dieta complicada. Solo un plan simple, con pasos
              claros, que puedes empezar hoy mismo con lo que ya tienes en tu
              cocina. Por menos de lo que cuesta un café.
            </p>

            <motion.a href="https://www.vitarutinas.com/cart/50330442137846:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="shimmer-btn w-full md:w-auto font-bold text-lg md:text-xl py-6 px-8 md:px-14 rounded-2xl shadow-xl flex items-center justify-center mx-auto group mb-3 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" animate={{
            scale: [1, 1.04, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} whileHover={{
            scale: 1.07
          }} whileTap={{
            scale: 0.97
          }} data-id="element-203">
              Empezar Hoy por $6.36 USD
            </motion.a>

            <div className="flex items-center justify-center gap-1.5" data-id="element-204">
              <LockIcon className="w-3.5 h-3.5 text-charcoal/50" data-id="element-205" />
              <span className="text-xs md:text-sm text-charcoal/60 font-semibold tracking-wide uppercase" data-id="element-206">
                Pago único — Sin suscripciones
              </span>
            </div>
          </FadeInUp>
        </div>
      </section>

      <FaqAccordion data-id="element-207" />

      {/* 8. FOOTER */}
      <footer className="py-12 bg-white border-t border-black/5" data-id="element-208">
        <div className="max-w-4xl mx-auto px-6 flex flex-col items-center text-center gap-6" data-id="element-209">
          <div className="flex items-center gap-6 text-sm" data-id="element-210">
            <button onClick={() => setModalOpen('privacy')} className="text-charcoal/60 hover:text-gold transition-colors font-medium" data-id="element-211">
              Política de Privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-212">•</span>
            <button onClick={() => setModalOpen('terms')} className="text-charcoal/60 hover:text-gold transition-colors font-medium" data-id="element-213">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-charcoal/50 text-sm max-w-2xl leading-relaxed" data-id="element-214">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar.
          </p>

          <p className="text-charcoal/40 text-sm" data-id="element-215">
            © {new Date().getFullYear()} Método Anti-Inflamatorio 7D — Todos
            los derechos reservados.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-216">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-217">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-218">
              <div className="flex items-center justify-between p-6 md:p-8 border-b border-black/5 bg-cream/50" data-id="element-219">
                <h3 className="font-serif text-2xl font-bold text-deep-green" data-id="element-220">
                  {modalOpen === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setModalOpen(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white hover:bg-black/5 transition-colors text-charcoal/60 shadow-sm" data-id="element-221">
                  <XIcon className="w-5 h-5" data-id="element-222" />
                </button>
              </div>
              <div className="p-6 md:p-8 overflow-y-auto text-charcoal/80 text-base leading-relaxed whitespace-pre-line font-sans" data-id="element-223">
                {modalOpen === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT}
              </div>
              <div className="p-6 md:p-8 border-t border-black/5 bg-cream/50" data-id="element-224">
                <button onClick={() => setModalOpen(null)} className="w-full py-4 bg-deep-green hover:bg-deep-green/90 rounded-2xl text-white font-bold text-lg transition-colors shadow-md" data-id="element-225">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}